/**
 * Per-column filter editor with type-aware filter modes.
 *
 * Renders inside a Popover. Mode selector offers only modes valid for the column type.
 * Each mode has a custom editor: text contains (+ regex), multi-select for categorical,
 * range for numbers/dates, element/length for arrays, missing/present for all.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  Checkbox,
  Flex,
  Stack,
  useTheme,
  fontSize,
  fontWeight,
  fontFamily,
  spacing,
} from '@fti/design-system';
import type { ColumnFilterPopoverProps } from './contracts';
import type { Filter, FilterScalar } from './model/types';
import { Popover } from './Popover';
import { getValue, getElementValues, isMissing } from './model/flatten';
import { toEpochMs, formatDate, formatNumber } from './format';

type FilterMode =
  | 'contains'
  | 'in'
  | 'range'
  | 'dateRange'
  | 'arrayElement'
  | 'arrayLength'
  | 'missing';

export function ColumnFilterPopover(props: ColumnFilterPopoverProps) {
  const { column, rows, indices, existing, anchor, onApply, onRemove, onClose } = props;
  const { theme } = useTheme();

  // Determine valid modes for this column type
  const validModes = useMemo(() => {
    const modes: FilterMode[] = ['missing'];
    if (column.type === 'text') {
      modes.unshift('contains');
      if (column.categorical) modes.unshift('in');
    } else if (column.type === 'boolean') {
      modes.unshift('in');
    } else if (column.type === 'number') {
      modes.unshift('range');
    } else if (column.type === 'date') {
      modes.unshift('dateRange');
    } else if (column.type === 'array') {
      modes.unshift('arrayLength', 'arrayElement');
    } else if (column.type === 'object' || column.type === 'mixed') {
      modes.unshift('contains');
    }
    return modes;
  }, [column.type, column.categorical]);

  // Initialize mode from existing filter or default
  const initialMode = useMemo(() => {
    if (existing) return existing.kind;
    return validModes[0];
  }, [existing, validModes]);

  const [mode, setMode] = useState<FilterMode>(initialMode);

  // State for each mode
  const [containsText, setContainsText] = useState('');
  const [containsRegex, setContainsRegex] = useState(false);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [arrayElementPath, setArrayElementPath] = useState('');
  const [arrayElementOp, setArrayElementOp] = useState<'eq' | 'contains'>('eq');
  const [arrayElementValue, setArrayElementValue] = useState('');
  const [arrayLengthMin, setArrayLengthMin] = useState('');
  const [arrayLengthMax, setArrayLengthMax] = useState('');
  const [missingPresent, setMissingPresent] = useState(true);
  const [valueSearch, setValueSearch] = useState('');

  // Initialize from existing filter
  useEffect(() => {
    if (!existing) return;
    if (existing.kind === 'contains') {
      setContainsText(existing.text);
      setContainsRegex(existing.regex ?? false);
    } else if (existing.kind === 'in') {
      setSelectedValues(new Set(existing.values.map(String)));
    } else if (existing.kind === 'range') {
      setRangeMin(existing.min !== undefined ? String(existing.min) : '');
      setRangeMax(existing.max !== undefined ? String(existing.max) : '');
    } else if (existing.kind === 'dateRange') {
      setDateFrom(existing.from ?? '');
      setDateTo(existing.to ?? '');
    } else if (existing.kind === 'arrayElement') {
      setArrayElementPath(existing.elementPath);
      setArrayElementOp(existing.op);
      setArrayElementValue(existing.value);
    } else if (existing.kind === 'arrayLength') {
      setArrayLengthMin(existing.min !== undefined ? String(existing.min) : '');
      setArrayLengthMax(existing.max !== undefined ? String(existing.max) : '');
    } else if (existing.kind === 'missing') {
      setMissingPresent(existing.present);
    }
  }, [existing]);

  // Compute value counts for categorical columns
  const valueCounts = useMemo(() => {
    if (mode !== 'in') return [];
    const counts = new Map<string, number>();
    for (const idx of indices) {
      const val = getValue(rows[idx], column);
      if (isMissing(val)) continue;
      const key = String(val);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 200);
  }, [mode, rows, indices, column]);

  // Compute observed range for number columns
  const observedRange = useMemo(() => {
    if (mode !== 'range') return null;
    let min = Infinity;
    let max = -Infinity;
    for (const idx of indices) {
      const val = getValue(rows[idx], column);
      if (typeof val === 'number') {
        min = Math.min(min, val);
        max = Math.max(max, val);
      }
    }
    return min <= max ? { min, max } : null;
  }, [mode, rows, indices, column]);

  // Compute observed date range
  const observedDateRange = useMemo(() => {
    if (mode !== 'dateRange') return null;
    let min = Infinity;
    let max = -Infinity;
    for (const idx of indices) {
      const val = getValue(rows[idx], column);
      const ms = toEpochMs(val);
      if (ms !== null) {
        min = Math.min(min, ms);
        max = Math.max(max, ms);
      }
    }
    if (min > max) return null;
    const minDate = new Date(min).toISOString().slice(0, 10);
    const maxDate = new Date(max).toISOString().slice(0, 10);
    return { min: minDate, max: maxDate };
  }, [mode, rows, indices, column]);

  // Compute top element values for array columns
  const topElementValues = useMemo(() => {
    if (mode !== 'arrayElement' || !arrayElementPath) return [];
    const counts = new Map<string, number>();
    for (const idx of indices) {
      const val = getValue(rows[idx], column);
      if (Array.isArray(val)) {
        const elems = getElementValues(val, arrayElementPath);
        for (const el of elems) {
          if (!isMissing(el)) {
            const key = String(el);
            counts.set(key, (counts.get(key) ?? 0) + 1);
          }
        }
      }
    }
    return [...counts.entries()]
      .map(([value]) => value)
      .sort()
      .slice(0, 30);
  }, [mode, arrayElementPath, rows, indices, column]);

  // Filter value list by search
  const filteredValues = useMemo(() => {
    if (!valueSearch) return valueCounts;
    const lower = valueSearch.toLowerCase();
    return valueCounts.filter((v) => v.value.toLowerCase().includes(lower));
  }, [valueCounts, valueSearch]);

  // Check if current state is valid
  const isValid = useMemo(() => {
    if (mode === 'contains') return containsText.length > 0;
    if (mode === 'in') return selectedValues.size > 0;
    if (mode === 'range') {
      const min = rangeMin ? parseFloat(rangeMin) : undefined;
      const max = rangeMax ? parseFloat(rangeMax) : undefined;
      if (min !== undefined && max !== undefined && min > max) return false;
      return min !== undefined || max !== undefined;
    }
    if (mode === 'dateRange') return dateFrom || dateTo;
    if (mode === 'arrayElement') return arrayElementPath && arrayElementValue;
    if (mode === 'arrayLength') {
      const min = arrayLengthMin ? parseInt(arrayLengthMin, 10) : undefined;
      const max = arrayLengthMax ? parseInt(arrayLengthMax, 10) : undefined;
      if (min !== undefined && max !== undefined && min > max) return false;
      return min !== undefined || max !== undefined;
    }
    if (mode === 'missing') return true;
    return false;
  }, [
    mode,
    containsText,
    selectedValues.size,
    rangeMin,
    rangeMax,
    dateFrom,
    dateTo,
    arrayElementPath,
    arrayElementValue,
    arrayLengthMin,
    arrayLengthMax,
  ]);

  const handleApply = () => {
    const id = existing?.id ?? crypto.randomUUID();
    let filter: Filter;

    if (mode === 'contains') {
      filter = {
        id,
        column: column.id,
        kind: 'contains',
        text: containsText,
        regex: containsRegex || undefined,
      };
    } else if (mode === 'in') {
      const values = [...selectedValues].map((v) => {
        // Try to parse as number or boolean
        const num = parseFloat(v);
        if (!isNaN(num) && String(num) === v) return num;
        if (v === 'true') return true;
        if (v === 'false') return false;
        return v;
      }) as FilterScalar[];
      filter = { id, column: column.id, kind: 'in', values };
    } else if (mode === 'range') {
      const min = rangeMin ? parseFloat(rangeMin) : undefined;
      const max = rangeMax ? parseFloat(rangeMax) : undefined;
      filter = { id, column: column.id, kind: 'range', min, max };
    } else if (mode === 'dateRange') {
      filter = {
        id,
        column: column.id,
        kind: 'dateRange',
        from: dateFrom || undefined,
        to: dateTo || undefined,
      };
    } else if (mode === 'arrayElement') {
      filter = {
        id,
        column: column.id,
        kind: 'arrayElement',
        elementPath: arrayElementPath,
        op: arrayElementOp,
        value: arrayElementValue,
      };
    } else if (mode === 'arrayLength') {
      const min = arrayLengthMin ? parseInt(arrayLengthMin, 10) : undefined;
      const max = arrayLengthMax ? parseInt(arrayLengthMax, 10) : undefined;
      filter = { id, column: column.id, kind: 'arrayLength', min, max };
    } else {
      filter = { id, column: column.id, kind: 'missing', present: missingPresent };
    }

    onApply(filter);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      e.preventDefault();
      handleApply();
    }
  };

  const handleSelectAll = () => {
    setSelectedValues(new Set(filteredValues.map((v) => v.value)));
  };

  const handleSelectNone = () => {
    setSelectedValues(new Set());
  };

  const modeOptions = validModes.map((m) => {
    const labels: Record<FilterMode, string> = {
      contains: 'Contains',
      in: 'Is one of',
      range: 'Range',
      dateRange: 'Date range',
      arrayElement: 'Element',
      arrayLength: 'Length',
      missing: 'Is missing / present',
    };
    return { value: m, label: labels[m] };
  });

  // Type badge text
  const typeBadge: Record<string, string> = {
    number: 'num',
    text: 'txt',
    date: 'date',
    boolean: 'bool',
    array: 'arr',
    object: 'obj',
    mixed: 'mix',
  };

  return (
    <Popover open anchor={anchor} onClose={onClose} width={340}>
      <Stack direction="vertical" gap="md">
        {/* Header */}
        <div>
          <div
            style={{
              fontFamily: fontFamily.mono,
              fontSize: fontSize.sm,
              fontWeight: fontWeight.semibold,
              color: theme.colors.text.primary,
            }}
          >
            {column.id}
          </div>
          <div
            style={{
              fontSize: fontSize.xs,
              color: theme.colors.text.secondary,
              marginTop: spacing.xs,
            }}
          >
            {typeBadge[column.type] || column.type}
          </div>
        </div>

        {/* Mode selector */}
        <Select
          options={modeOptions}
          value={mode}
          onChange={(v) => setMode(v as FilterMode)}
          size="sm"
        />

        {/* Editor per mode */}
        <div>
          {mode === 'contains' && (
            <Stack direction="vertical" gap="sm">
              <Input
                type="text"
                value={containsText}
                onChange={(e) => setContainsText(e.target.value)}
                placeholder="Search text"
                size="sm"
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <Checkbox
                checked={containsRegex}
                onChange={(e) => setContainsRegex(e.target.checked)}
                label="Regex"
              />
            </Stack>
          )}

          {mode === 'in' && (
            <Stack direction="vertical" gap="sm">
              <Input
                type="text"
                value={valueSearch}
                onChange={(e) => setValueSearch(e.target.value)}
                placeholder="Search values"
                size="sm"
              />
              <Flex gap="sm" justify="between">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.primary[500],
                    fontSize: fontSize.sm,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={handleSelectNone}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.primary[500],
                    fontSize: fontSize.sm,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  None
                </button>
              </Flex>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '4px',
                  padding: spacing.sm,
                }}
              >
                <Stack direction="vertical" gap="xs">
                  {filteredValues.map((v) => (
                    <Checkbox
                      key={v.value}
                      checked={selectedValues.has(v.value)}
                      onChange={(e) => {
                        const next = new Set(selectedValues);
                        if (e.target.checked) next.add(v.value);
                        else next.delete(v.value);
                        setSelectedValues(next);
                      }}
                      label={`${v.value} (${v.count})`}
                    />
                  ))}
                </Stack>
              </div>
            </Stack>
          )}

          {mode === 'range' && (
            <Stack direction="vertical" gap="sm">
              <Input
                type="number"
                value={rangeMin}
                onChange={(e) => setRangeMin(e.target.value)}
                placeholder="Min"
                size="sm"
                onKeyDown={handleKeyDown}
                helperText={
                  observedRange ? `Observed: ${formatNumber(observedRange.min)} – ${formatNumber(observedRange.max)}` : undefined
                }
              />
              <Input
                type="number"
                value={rangeMax}
                onChange={(e) => setRangeMax(e.target.value)}
                placeholder="Max"
                size="sm"
                onKeyDown={handleKeyDown}
              />
            </Stack>
          )}

          {mode === 'dateRange' && (
            <Stack direction="vertical" gap="sm">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
                size="sm"
                onKeyDown={handleKeyDown}
                helperText={
                  observedDateRange ? `Observed: ${observedDateRange.min} – ${observedDateRange.max}` : undefined
                }
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
                size="sm"
                onKeyDown={handleKeyDown}
              />
            </Stack>
          )}

          {mode === 'arrayElement' && (
            <Stack direction="vertical" gap="sm">
              {/* The DS Select treats '' as no selection, so the element itself uses a '.' sentinel. */}
              <Select
                options={column.elementPaths.map((p) => ({ value: p || '.', label: p || '(element)' }))}
                value={arrayElementPath === '' && column.elementPaths.includes('') ? '.' : arrayElementPath}
                onChange={(v) => setArrayElementPath(v === '.' ? '' : v)}
                placeholder="Select element path"
                size="sm"
              />
              <Select
                options={[
                  { value: 'eq', label: 'equals' },
                  { value: 'contains', label: 'contains' },
                ]}
                value={arrayElementOp}
                onChange={(v) => setArrayElementOp(v as 'eq' | 'contains')}
                size="sm"
              />
              <Input
                type="text"
                value={arrayElementValue}
                onChange={(e) => setArrayElementValue(e.target.value)}
                placeholder="Value"
                size="sm"
                onKeyDown={handleKeyDown}
                list={`datalist-${column.id}`}
              />
              <datalist id={`datalist-${column.id}`}>
                {topElementValues.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </Stack>
          )}

          {mode === 'arrayLength' && (
            <Stack direction="vertical" gap="sm">
              <Input
                type="number"
                value={arrayLengthMin}
                onChange={(e) => setArrayLengthMin(e.target.value)}
                placeholder="Min length"
                size="sm"
                onKeyDown={handleKeyDown}
              />
              <Input
                type="number"
                value={arrayLengthMax}
                onChange={(e) => setArrayLengthMax(e.target.value)}
                placeholder="Max length"
                size="sm"
                onKeyDown={handleKeyDown}
              />
            </Stack>
          )}

          {mode === 'missing' && (
            <Stack direction="vertical" gap="sm">
              <Checkbox
                checked={!missingPresent}
                onChange={(e) => setMissingPresent(!e.target.checked)}
                label="Is missing"
              />
              <Checkbox
                checked={missingPresent}
                onChange={(e) => setMissingPresent(e.target.checked)}
                label="Is present"
              />
            </Stack>
          )}
        </div>

        {/* Footer */}
        <Flex gap="sm" justify="between">
          <Flex gap="sm">
            <Button variant="solid" size="sm" onClick={handleApply} disabled={!isValid}>
              Apply
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </Flex>
          {existing && (
            <Button variant="ghost" size="sm" color="error" onClick={onRemove}>
              Remove
            </Button>
          )}
        </Flex>
      </Stack>
    </Popover>
  );
}
